import React, { ComponentProps, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  Flex,
  Box,
  Icon,
  PopupMenu,
  ItemType,
  Text,
  Space,
  getCustomDomainComponents,
  utils,
} from "@thenounproject/lingo-core";

import { InsertType } from "../../constants/InsertType";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useCreateFileAssets from "@redux/actions/items/useCreateFileAssets";
import useCreateAssetsFromSource, {
  AssetSourceVendors,
  ExternalSource,
} from "@redux/actions/items/useCreateAssetsFromSource";
import { type InsertPosition } from "@actions/uploads";
import { useSelectSpace } from "@selectors/entities/spaces";
import useGoogleDrivePicker from "@hooks/useGoogleDrivePicker";
import useDropboxPicker from "@hooks/useDropboxPicker";
import useBatchSaveItems from "@redux/actions/items/useBatchSaveItems";
import { insertData as insertDataMap } from "../../constants/InsertType";

const MenuItem = styled.button`
  display: flex;
  padding: 8px 16px;
  background: transparent;

  transition: background 0.3s ease;
  cursor: grap;

  &:hover {
    background: ${utils.getColor("grayLighter")};
  }
`;

type Props = {
  insertPosition: InsertPosition;
  // If showing manually from a source (should not be provided with coordinates)
  sourceId?: string;
  position?: Pick<ComponentProps<typeof PopupMenu>, "vPos" | "hPos">;
  coordinates?: { xPos: number | string; yPos: number | string };
  close: () => void;
  startEditingItem?: (itemId: string) => void;
};

type Context = { space: Space; insertPosition: InsertPosition };

const InsertAssetMenu: React.FC<Props> = ({
  insertPosition,
  coordinates,
  close,
  sourceId = "insert-asset-menu",
  position = {},
  startEditingItem,
}) => {
  const space = useSelectSpace();

  const { showModal } = useShowModal(),
    [createFileAssets] = useCreateFileAssets(),
    [createAssetsFromSource] = useCreateAssetsFromSource(),
    [batchSaveItems] = useBatchSaveItems();

  const onSelectFiles = useCallback(
    (files: File[]) => createFileAssets({ files, itemType: ItemType.asset, insertPosition }),
    [createFileAssets, insertPosition]
  );

  const onSelectSources = useCallback(
    (sources: ExternalSource[]) =>
      void createAssetsFromSource({
        sources,
        insertPosition,
      }),
    [createAssetsFromSource, insertPosition]
  );

  const onPickGoogleFont = useCallback(
    (names: string[]) => {
      const sources = names.map(name => ({
        name,
        source: AssetSourceVendors.GoogleFonts,
      }));
      return createAssetsFromSource({ sources, insertPosition });
    },
    [createAssetsFromSource, insertPosition]
  );

  const _openGooglePicker = useGoogleDrivePicker();
  const openGooglePicker = useCallback(() => {
    _openGooglePicker({
      multiselect: true,
      onSuccess: sources => {
        void createAssetsFromSource({ sources, insertPosition });
      },
    });
  }, [_openGooglePicker, createAssetsFromSource, insertPosition]);

  const _openDropboxPicker = useDropboxPicker();
  const openDropboxPicker = useCallback(() => {
    _openDropboxPicker({
      multiselect: true,
      onSuccess: sources => {
        void createAssetsFromSource({ sources, insertPosition });
      },
    });
  }, [_openDropboxPicker, createAssetsFromSource, insertPosition]);

  const onCreateCodeSnippet = useCallback(async () => {
    const insertData = insertDataMap[InsertType.code];
    const newItem = {
      kit_uuid: insertPosition.kitId,
      section_uuid: insertPosition.sectionId,
      display_order: insertPosition.displayOrder,
      type: insertData.type,
      ...insertData,
    };

    const res = await batchSaveItems({
      updatedItems: [],
      insertConfig: {
        sectionId: insertPosition.sectionId,
        newItems: [newItem],
        insertIndex: insertPosition.insertIndex,
        targetIndex: insertPosition.insertIndex,
        insertType: InsertType.code,
      },
    });

    const { response: { newItems } = {}, error: responseError } = res;
    if (!responseError && newItems && newItems.length > 0 && startEditingItem) {
      const createdItem = newItems[0];
      startEditingItem(createdItem.uuid);
    }

    close();
  }, [batchSaveItems, insertPosition, close, startEditingItem]);

  const assetMenuItems = useMemo(() => {
    const { host } = getCustomDomainComponents(),
      hideGoogleImport = Boolean(host);
    return [
      { title: "-Create" },
      {
        type: InsertType.file,
        title: "File",
        icon: "content.file",
        label: "Add images, videos, docs and more",
        modal: ModalTypes.PICK_FILE,
      },
      {
        type: InsertType.color,
        title: "Color",
        icon: "content.color",
        label: "Add digital and print color values",
        modal: ModalTypes.CREATE_EDIT_COLOR,
      },
      {
        type: InsertType.font,
        title: "Font",
        icon: "content.font",
        label: "Add font files or use google fonts",
        modal: ModalTypes.CREATE_FONT,
      },
      {
        type: InsertType.link,
        title: "Link",
        icon: "action.link",
        label: "Add links to external resources",
        modal: ModalTypes.CREATE_EDIT_LINK,
      },
      {
        type: InsertType.code,
        title: "Code",
        icon: "content.code",
        label: "Add a code snippet",
        onClick: onCreateCodeSnippet,
        isHidden: () => !insertPosition.kitId || !!insertPosition.itemId, // hide in library context
      },
      {
        title: "-Import",
      },
      {
        type: InsertType.library,
        title: "From Library",
        icon: "marketing.storage",
        label: "Add assets from your library",
        modal: ModalTypes.LIBRARY_PICKER,
        isHidden: ({ space, insertPosition }: Context) => {
          return !(
            insertPosition.kitId && space?.access?.permissions?.includes("view_asset_library")
          );
        },
      },
      {
        title: "-",
      },
      {
        type: InsertType.figma,
        title: "Figma",
        icon: "integrations.figma",
        label: "Add assets from a Figma file",
        modal: ModalTypes.FIGMA_LINK_MODAL,
      },
      {
        type: "GOOGLE",
        isHidden: () => hideGoogleImport,
        title: "Google Drive",
        label: "Import assets from Google drive",
        icon: "integrations.google-drive",
        onClick: openGooglePicker,
      },
      {
        type: "DROPBOX",
        title: "Dropbox",
        label: "Import assets from Dropbox",
        icon: "integrations.dropbox",
        onClick: openDropboxPicker,
      },
    ];
  }, [openDropboxPicker, openGooglePicker, insertPosition, onCreateCodeSnippet]);

  const menuItemModalProps = {
    [InsertType.file]: {
      onUploadFiles: onSelectFiles,
      onImportFiles: onSelectSources,
      itemType: ItemType.asset,
      multiple: true,
      insertPosition,
    },
    [InsertType.font]: { onPickFontFiles: onSelectFiles, onPickGoogleFont },
    [InsertType.color]: { insertPosition },
    [InsertType.figma]: { insertPosition },
    [InsertType.library]: { insertPosition },
    [InsertType.link]: { insertPosition },
    [InsertType.code]: { insertPosition },
  };

  const menuItems = useMemo(
    () => assetMenuItems.filter(item => item.isHidden?.({ space, insertPosition }) !== true),
    [assetMenuItems, insertPosition, space]
  );

  const menu = (
    <PopupMenu
      width={320}
      source={sourceId}
      close={close}
      vPos="floatBelow"
      hPos="alignLeft"
      maxHeight={550}
      {...position}>
      <Flex background="white" flexDirection="column" width="100%" pb="s">
        {menuItems.map((item, idx) => {
          if (item.title === "-") {
            return <Box flexShrink="0" key={`divider-${idx}`} width="100%" height="1px" />;
          } else if (item.title.startsWith("-")) {
            const label = item.title.replace("-", "");
            return (
              <Text key={`title-${label}`} px="m" mt="l" font="ui.smallBold" color="grayDarkest">
                {label}
              </Text>
            );
          }

          return (
            <MenuItem
              data-testid={"asset-menu-item-".concat(item.type).toLowerCase()}
              key={item.type}
              onClick={
                item.onClick ?? (() => showModal(item.modal, menuItemModalProps[item.type] || {}))
              }>
              <Flex
                borderRadius="default"
                flexShrink="0"
                background="white"
                boxShadow="button"
                justifyContent="center"
                alignItems="center"
                height="40px"
                width="40px"
                mr="s">
                <Icon iconId={item.icon} />
              </Flex>
              <Flex flexDirection="column" textAlign="left">
                <Flex alignItems="center">
                  <Text font="ui.regular">{item.title}</Text>
                </Flex>
                <Text font="ui.small" color="grayDarkest">
                  {item.label}
                </Text>
              </Flex>
            </MenuItem>
          );
        })}
      </Flex>
    </PopupMenu>
  );

  return coordinates ? (
    <PopupMenu.ContextMenuAnchor data-popup-source={sourceId} {...coordinates}>
      {menu}
    </PopupMenu.ContextMenuAnchor>
  ) : (
    menu
  );
};

export default InsertAssetMenu;
